import { Indicator, Root } from '@radix-ui/react-checkbox';
import type { ComponentPropsWithoutRef, ElementRef, ReactNode } from 'react';
import { forwardRef } from 'react';
import { BaseFillCheckboxHalfpick } from '@/app/components/icons/BaseFillCheckboxHalfpick';
import { BaseFillCheckPick2 } from '@/app/components/icons/BaseFillCheckPick2';
import { BaseFillCheckUnpick } from '@/app/components/icons/BaseFillCheckUnpick';
import { cn } from '@/app/utils';

/**
 * 组件：填充风格复选框
 *
 * @remarks
 * 该组件基于 Radix UI 的 Checkbox 组件封装，提供了三种状态：
 * - 未选中：显示 BaseFillCheckUnpick 图标
 * - 选中：显示 BaseFillCheckPick2 图标
 * - 中间态：显示 BaseFillCheckboxHalfpick 图标
 * 组件继承了 Radix UI Checkbox 的所有属性和事件，并保持完整的可访问性支持。
 *
 * @example
 * // 基础用法
 * <CheckboxFill checked={isChecked} onCheckedChange={handleChange} />
 *
 * // 带中间态
 * <CheckboxFill
 *   checked={isIndeterminate ? 'indeterminate' : isChecked}
 *   onCheckedChange={handleChange}
 * />
 */
const CheckboxFill = forwardRef<
  ElementRef<typeof Root>,
  ComponentPropsWithoutRef<typeof Root> & {
    customIcon?: ReactNode;
  }
>(({ className, customIcon, ...props }, ref) => (
  <Root ref={ref} className={cn('group peer shrink-0', className)} {...props}>
    <Indicator>
      {/* 根据 checked 状态切换显示不同的图标 */}
      {customIcon ? (
        customIcon
      ) : props.checked === 'indeterminate' ? (
        <BaseFillCheckboxHalfpick color={props?.color} />
      ) : (
        <BaseFillCheckPick2 color={props?.color} />
      )}
    </Indicator>
    {/* 未选中状态的图标，在选中或中间态时隐藏 */}
    <BaseFillCheckUnpick className="group-data-[state=checked]:hidden group-data-[state=indeterminate]:hidden" />
  </Root>
));

CheckboxFill.displayName = 'CheckboxFill';

export { CheckboxFill };
